/**
 * Warms the browser cache with a newer build's entry graph before the tab
 * reloads onto it, so the reload is served from cache instead of the network.
 *
 * Hashed assets ship `Cache-Control: immutable`, so whatever is fetched here
 * is exactly what the reloaded document will ask for. The links are appended
 * with the same `crossorigin` the build emits: the HTTP cache keys on request
 * mode, and a preload fetched without CORS cannot satisfy a module script
 * fetched with it — the warm-up would cost bandwidth and buy nothing.
 */

export interface PreloadLink {
  rel: 'modulepreload' | 'prefetch';
  href: string;
  as?: 'style';
  crossOrigin: boolean;
}

const WARM_ATTR = 'data-kula-warm';

/** Same-origin absolute path, or null — nothing else is ours to preload. */
const ownPath = (url: string | null): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin ? parsed.pathname : null;
  } catch {
    return null;
  }
};

/**
 * The entry graph a build's index.html declares: its module script(s), what
 * it modulepreloads, and its stylesheets. Pure, so the parsing is testable
 * without touching the document.
 */
export const preloadLinksFrom = (html: string): PreloadLink[] => {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return [];
  }

  const links: PreloadLink[] = [];
  const seen = new Set<string>();
  const push = (link: PreloadLink) => {
    if (seen.has(link.href)) return;
    seen.add(link.href);
    links.push(link);
  };

  doc
    .querySelectorAll<HTMLScriptElement>('script[type="module"][src]')
    .forEach((el) => {
      const href = ownPath(el.getAttribute('src'));
      if (href)
        push({
          rel: 'modulepreload',
          href,
          crossOrigin: el.hasAttribute('crossorigin'),
        });
    });
  doc
    .querySelectorAll<HTMLLinkElement>('link[rel="modulepreload"][href]')
    .forEach((el) => {
      const href = ownPath(el.getAttribute('href'));
      if (href)
        push({
          rel: 'modulepreload',
          href,
          crossOrigin: el.hasAttribute('crossorigin'),
        });
    });
  doc
    .querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]')
    .forEach((el) => {
      const href = ownPath(el.getAttribute('href'));
      if (href)
        push({
          rel: 'prefetch',
          href,
          as: 'style',
          crossOrigin: el.hasAttribute('crossorigin'),
        });
    });

  return links;
};

/**
 * Append the preload links for `buildId`, skipping any href already present
 * in the document (the current build's own tags, or an earlier warm-up).
 * Returns how many were added.
 */
export const injectPreloads = (
  links: PreloadLink[],
  buildId: string
): number => {
  const present = new Set(
    Array.from(
      document.querySelectorAll<HTMLElement>('link[href], script[src]')
    ).map((el) => ownPath(el.getAttribute('href') ?? el.getAttribute('src')))
  );

  let added = 0;
  for (const link of links) {
    if (present.has(link.href)) continue;
    const el = document.createElement('link');
    el.rel = link.rel;
    el.href = link.href;
    if (link.as) el.as = link.as;
    if (link.crossOrigin) el.crossOrigin = 'anonymous';
    el.setAttribute(WARM_ATTR, buildId);
    document.head.appendChild(el);
    present.add(link.href);
    added += 1;
  }
  return added;
};

/**
 * Fetch the live index.html and warm its entry graph. Silent on any failure:
 * a cold reload is still a correct reload, just a slower one.
 */
export const warmBuild = async (buildId: string): Promise<number> => {
  try {
    const res = await fetch(`${window.location.origin}/index.html`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) return 0;
    return injectPreloads(preloadLinksFrom(await res.text()), buildId);
  } catch {
    return 0;
  }
};
